import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, tap } from 'rxjs';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { CommonService } from 'src/app/shared/common.service';
import { IPageCount } from 'src/app/shared/models/page.model';
import { INewCommissionDTO, INewCommissionStep } from '../../../models/commissions.models';
import { FetchBrands } from 'src/app/features/brands/store/brands.actions';
import { FetchTags } from 'src/app/features/tags/store/tags.actions';
import { FetchCategories } from 'src/app/features/categories/store/categories.actions';
import { AddCommission, AddCommissionSuccess, GetAgents } from '../../../store/agents.actions';
import { parseAgentFilterRequest, refillAgentFilter } from 'src/app/shared/utils/helpers';
import { IAgentFilter } from '../../../models/agents.models';

@Component({
  selector: 'app-new-commission',
  templateUrl: './new-commission.component.html',
  styleUrls: ['./new-commission.component.scss']
})
export class NewCommissionComponent implements OnInit {
creationSteps: INewCommissionStep[] = [
    { label: 'commission.new.details', active: true, completed: false },
    { label: 'commission.new.summary.thresholds', active: false, completed: false },
    { label: 'commission.new.negotiation', active: false, completed: false },
    { label: 'commission.new.summary.title', active: false, completed: false },
  ];

  activeStep!: INewCommissionStep;
  currentStepIndex = 0;

  // Some default values for summary interactive preview
  commissionPayload: INewCommissionDTO = {
    name: null,
    description: null,
    commission_type: 'base',
    calculation_method: 'order_discounted',
    expiry_date: null,
    commission: 0,
    brand_ids: [],
    label_ids: [],
    category_ids: [],
    thresholds: [],
    negotiations: [],
    agent_ids: [],
    status: 'active'
  };

  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1,
  };

  destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private commonService: CommonService,
    private translateService: TranslateService,
    private actions: Actions
  ) {}

  ngOnInit(): void {
    this.activeStep = this.creationSteps[0];

    // Fetch all brands, labels and categories
    this.store.dispatch(FetchBrands({page: {number: 0, size: 0}}));
    this.store.dispatch(FetchTags({page: {number: 0, size: 0}}));
    this.store.dispatch(FetchCategories({page: {number: 0, size: 0}}));
    const filter:IAgentFilter = refillAgentFilter()
    filter.extraInfoFilter.has_base_commission = false
    this.store.dispatch(GetAgents({filter: parseAgentFilterRequest(filter)}));

    this.actions
      .pipe(
        ofType(AddCommissionSuccess),
        tap(() => {
          this.router.navigate(['/agents/commissions']);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  get CommissionDetailsIsComplete() {
    return this.creationSteps[0].completed;
  }

  goToStep(index: number) {
    if (
      this.creationSteps[index].completed ||
      index !== this.currentStepIndex
    ) {
      this.creationSteps[this.currentStepIndex].active = false;
      this.currentStepIndex = index;
      this.creationSteps[this.currentStepIndex].active = true;
    }
  }

  previous(payload: Partial<INewCommissionDTO>) {
    this.completeStep(payload);

    if (this.currentStepIndex - 1 >= 0) {
      this.currentStepIndex--;
      this.creationSteps[this.currentStepIndex].active = true;
      this.goToStep(this.currentStepIndex);
    }
  }

  next(payload: Partial<INewCommissionDTO>) {
    this.completeStep(payload);

    if (this.currentStepIndex + 1 < this.creationSteps.length) {
      this.currentStepIndex++;
      this.creationSteps[this.currentStepIndex].active = true;
      this.goToStep(this.currentStepIndex);
    } else {
      if (!this.CommissionDetailsIsComplete) {
        this.commonService.showToast(
          this.translateService.instant('alerts.wizard.uncomplete_steps', { steps : '(' + this.translateService.instant('commission.new.details') + ')'}),
          'WARNING',
          { duration: 3000 }
        );
        return;
      }

      console.log('Commission Payload', this.commissionPayload);
      this.store.dispatch(AddCommission({ commission: this.commissionPayload }));
    }
  }

  completeStep(payload: Partial<INewCommissionDTO>) {
    this.commissionPayload = { ...this.commissionPayload, ...payload };
    const activeStep = this.creationSteps[this.currentStepIndex];
    activeStep.completed = true;
    activeStep.active = false;
  }

  goBack() {
    this.router.navigate(['/agents/commissions']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
